// Worker <-> API internal contract for checkpoints, runner failures and crash
// recovery (contract docs/plans/2026-10-02-checkpoints-contract.md §4). All
// routes live under `/internal` with the `X-Internal-Secret` header, like
// routes/internal/pauses.ts. Blobs travel base64 (plaintext); gzip +
// envelope-encryption happen API-side only.

import { z } from 'zod'
import { RunnerFailureSchema } from './failure.js'
import { CheckpointTriggerSchema, RunCheckpointKindSchema, RunPauseReasonSchema, RunWorklogSchema } from './checkpoint.js'

/** Same as routes/internal/pauses.ts's `SAVE_PAUSE_BODY_LIMIT_BYTES`: 20 MB plaintext patch ≈ 27 MB base64. */
export const CHECKPOINT_BODY_LIMIT_BYTES = 30 * 1024 * 1024
/** Plaintext workspace patch above this: no checkpoint, a `checkpoint_saved {skipped: 'too_large'}` event instead (matches worker pause/snapshot.ts). */
export const CHECKPOINT_MAX_PATCH_BYTES = 20 * 1024 * 1024

// ---------------------------------------------------------------------------
// POST /internal/runs/:runId/checkpoints
// ---------------------------------------------------------------------------

/**
 * A `periodic` or `failure` checkpoint. (`pause` checkpoints keep going
 * through `POST /internal/runs/:runId/pause`, which also moves the run;
 * `handoff` is only ever set by the API.) A `periodic` one replaces the run's
 * previous `periodic` row (upsert on the partial unique index).
 */
export const SaveCheckpointRequestSchema = z
  .object({
    kind: z.enum(['periodic', 'failure']),
    trigger: CheckpointTriggerSchema,
    /** Worker `ExecutorKind`: 'claude-cli' | 'codex-cli' | 'pi'. */
    runner: z.string().min(1),
    agentId: z.string().uuid(),
    /** The executor connection (`payload.executor.credentialProfileId`). */
    connectionId: z.string().uuid().optional(),
    /** Claude Code's session id; Pi uses the fixed `PI_SESSION_FILE` and leaves this unset. */
    sessionId: z.string().optional(),
    sessionBlobBase64: z.string().optional(),
    workspacePatchBase64: z.string().optional(),
    baseCommit: z.string().optional(),
    /** sha256 hex of the plaintext patch — the worker skips a periodic checkpoint when this and the session size are unchanged. */
    diffHash: z.string().optional(),
    /** `UsageTotals` (worker guard/usage-meter.ts): {modelCalls, tokens, cacheReadTokens, costUsd}. */
    usage: z.record(z.string(), z.unknown()).optional(),
    /** Short "where the run was" text (worker pause/summary.ts style). */
    summary: z.string().optional(),
    worklog: RunWorklogSchema.optional(),
    /** Required for `kind: 'failure'` — what went wrong (any `RunnerFailure.kind`, incl. `fatal`). */
    failure: RunnerFailureSchema.optional(),
  })
  .refine((body) => body.kind !== 'failure' || body.failure !== undefined, {
    message: 'A failure checkpoint needs `failure`',
    path: ['failure'],
  })
export type SaveCheckpointRequest = z.infer<typeof SaveCheckpointRequestSchema>

export const SaveCheckpointResponseSchema = z.object({
  checkpointId: z.string().uuid(),
  kind: RunCheckpointKindSchema,
  createdAt: z.string(),
  expiresAt: z.string(),
})
export type SaveCheckpointResponse = z.infer<typeof SaveCheckpointResponseSchema>

// ---------------------------------------------------------------------------
// GET /internal/runs/:runId/checkpoints/:checkpointId
// ---------------------------------------------------------------------------

/**
 * One checkpoint, decrypted, any kind. `:runId` is the *requesting* run: the
 * checkpoint must belong to it or (handoff) to one of its ancestors via
 * `parent_run_id`; otherwise 404. Blobs are null once wiped (resumed, expired
 * or past retention).
 */
export const RunCheckpointSnapshotSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  kind: RunCheckpointKindSchema,
  /** Kind `pause` only. */
  reason: RunPauseReasonSchema.nullable(),
  /** Kind `pause` only: the approval whose decision resumes it. */
  approvalId: z.string().uuid().nullable(),
  toolCallId: z.string().nullable(),
  runner: z.string(),
  agentId: z.string().uuid().nullable(),
  connectionId: z.string().uuid().nullable(),
  sessionId: z.string().nullable(),
  sessionBlobBase64: z.string().nullable(),
  workspacePatchBase64: z.string().nullable(),
  baseCommit: z.string().nullable(),
  diffHash: z.string().nullable(),
  usage: z.record(z.string(), z.unknown()).nullable(),
  summary: z.string().nullable(),
  worklog: RunWorklogSchema.nullable(),
  failure: RunnerFailureSchema.nullable(),
  resumeAfter: z.string().nullable(),
  expiresAt: z.string(),
  resumedAt: z.string().nullable(),
  createdAt: z.string(),
})
export type RunCheckpointSnapshot = z.infer<typeof RunCheckpointSnapshotSchema>

// POST /internal/runs/:runId/checkpoints/:checkpointId/resumed
// Body `{}`; response `{ok: true}`. Marks it resumed and wipes its blobs —
// only *after* the restore succeeded (same rule as the pause route). Also
// called by a handoff child for its parent's checkpoint. Idempotent.

// ---------------------------------------------------------------------------
// POST /internal/runs/:runId/runner-failure
// ---------------------------------------------------------------------------

/**
 * The worker reports a `quota`, `rate_limit` (retries used up) or `auth`
 * failure *after* writing its `failure` checkpoint. The API marks the
 * connection, applies the role's `onQuota` policy, moves the run and records
 * the timeline events. Once this returns, the run is the API's: the worker
 * tears down without persisting a result, transitioning or calling
 * `delivered`. If the call fails, the worker falls back to failing the run
 * itself (checkpoint already saved, so "resume" still works).
 */
export const ReportRunnerFailureRequestSchema = z.object({
  failure: RunnerFailureSchema.refine((f) => f.kind === 'quota' || f.kind === 'rate_limit' || f.kind === 'auth', {
    message: 'Only quota, rate_limit and auth failures are reported to the API',
  }),
  /** The failure checkpoint just saved; null when none could be saved (e.g. patch too large). */
  checkpointId: z.string().uuid().nullable(),
})
export type ReportRunnerFailureRequest = z.infer<typeof ReportRunnerFailureRequestSchema>

export const RunnerFailureActionSchema = z.enum(['wait', 'handoff', 'blocked', 'failed'])
export type RunnerFailureAction = z.infer<typeof RunnerFailureActionSchema>

export const ReportRunnerFailureResponseSchema = z.object({
  /** `wait`: run `paused` until `resumeAfter`. `handoff`: run `handed_off`, `childRunId` queued. `blocked`: auth, or no fallback and mode `handoff`. `failed`: the run could not be moved (e.g. already cancelled) — nothing for the worker to do either way. */
  action: RunnerFailureActionSchema,
  resumeAfter: z.string().nullable(),
  childRunId: z.string().uuid().nullable(),
  /** One line for the worker's log. */
  message: z.string(),
})
export type ReportRunnerFailureResponse = z.infer<typeof ReportRunnerFailureResponseSchema>

// ---------------------------------------------------------------------------
// POST /internal/runs/:runId/recover
// ---------------------------------------------------------------------------

/**
 * Crash recovery (design §3.6). The worker's lease reaper calls this for each
 * run whose lease expired (after releasing that lease), instead of writing
 * `status = 'queued'` and re-enqueueing `config_snapshot` itself. The API
 * moves the run back to `queued`, rebuilds the payload via
 * `RunDispatchService.buildPayload` (fixing the old parse bug), adds
 * `resume: {checkpointId, via: 'crash'}` when the run has a usable
 * checkpoint, and enqueues it — or fails the run after `MAX_CRASH_RESUMES`.
 */
export const RecoverRunRequestSchema = z.object({
  workerId: z.string().min(1),
  reason: z.literal('lease_expired'),
})
export type RecoverRunRequest = z.infer<typeof RecoverRunRequestSchema>

export const RecoverRunResponseSchema = z.object({
  /** `resumed`: from `checkpointId`. `restarted`: no checkpoint, from scratch. `failed`: recovery limit reached. `skipped`: the run was no longer in a recoverable status. */
  action: z.enum(['resumed', 'restarted', 'failed', 'skipped']),
  checkpointId: z.string().uuid().nullable(),
})
export type RecoverRunResponse = z.infer<typeof RecoverRunResponseSchema>
